import { Icon } from '@mirai-kids-monte/core/src/components/dataDisplay/Icon';
import clsx from 'clsx';
import Link from 'next/link';
import { useCallback, useEffect, useState, type ComponentProps, type RefObject } from 'react';
import HomeMontessoriLogoType from '../../../../public/img/home-montessori-logo-type.svg';
import NurseryWorksLogoType from '../../../../public/img/nursery-works-logo-type.svg';
import { ContentSize } from '../../../constants/ContentSize';
import { FontSize } from '../../../constants/FontSize';
import { PageUrl } from '../../../constants/PageUrl';
import { SectionId } from '../../../constants/SectionId';
import { useModalMenuA11y } from '../../../hooks/useModalMenuA11y';
import Color, { type ColorType } from '../../../styles/Color';
import LineButton from '../../class_landing_page/atoms/LineButton';
import MainButton from '../../class_landing_page/atoms/MainButton';
import { TelephoneButton } from '../../class_landing_page/atoms/TelephoneButton';
import HamburgerMenuButton from '../../class_landing_page/molecules/HamburgerMenuButton';
import styles from './index.module.scss';

const LOGO_LINK_LABELS: Record<string, string> = {
  [PageUrl.TOP]: 'みらいキッズモンテ トップへ',
  [PageUrl.DAYCARE_TOP]: 'みらいキッズモンテ 保育園トップへ',
  [PageUrl.CLASS_TOP]: 'みらいキッズモンテ 教室トップへ',
  [PageUrl.MONTE_CHAT_ROOT]: 'みらいキッズモンテチャット トップへ',
  [PageUrl.TOOL_RECOMMENDATION_ROOT]: 'みらいキッズ教具選び支援 トップへ',
  [PageUrl.DIAGNOSIS_ROOT]: '無料観察サポート診断 トップへ',
  [PageUrl.NURSERY_DX_SUPPORT_ROOT]: 'みらいキッズWORKS トップへ',
  [PageUrl.TV_LP]: 'このページのトップへ',
};

type HeaderProps = {
  variant?: 'default' | 'thin';
  showMenuButton?: boolean;
  logotypeType?: 'school' | 'homeMontessori' | 'nurseryWorks';
  logoHref?: string;
  menuList?: ComponentProps<typeof Menu>['list'];
  buttonList?: ComponentProps<typeof Menu>['buttonList'];
  hamburgerAccentColor?: string;
};

export const Header = ({
  variant = 'default',
  showMenuButton = true,
  logotypeType = 'school',
  menuList,
  buttonList,
  logoHref = PageUrl.TOP,
  hamburgerAccentColor,
}: HeaderProps) => {
  const [isScrollOver, setIsScrollOver] = useState(false);
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const closeMenu = useCallback(() => {
    setIsOpenMenu(false);
  }, []);

  const { menuId, menuRef, triggerRef } = useModalMenuA11y({ isOpen: isOpenMenu, onClose: closeMenu });

  const isVariantThin = variant === 'thin' || isScrollOver || isOpenMenu;
  const headerHeightPx = isVariantThin ? ContentSize.HEADER_THIN_HEIGHT_PX : ContentSize.HEADER_HEIGHT_PX;

  useEffect(() => {
    const scrollAction = () => {
      if (window.scrollY < headerHeightPx) {
        setIsScrollOver(false);
      } else {
        setIsScrollOver(true);
      }
    };

    window.addEventListener('scroll', scrollAction, {
      capture: false,
      passive: true,
    });
    scrollAction();

    return () => {
      window.removeEventListener('scroll', scrollAction);
    };
  }, [headerHeightPx]);

  const headerClassName = clsx(styles.header, isVariantThin ? styles['header-thin'] : styles['header-default']);

  const logoMarkClassName = clsx(
    styles['logo-mark-wrapper'],
    isVariantThin ? styles['logo-mark-thin'] : styles['logo-mark-default']
  );

  const logoTypeClassName = clsx(
    styles['logo-type-wrapper'],
    isVariantThin ? styles['logo-type-thin'] : styles['logo-type-default']
  );

  const schoolLogoClassName = clsx(
    styles['school-logo-type'],
    isVariantThin ? styles['school-logo-type-thin'] : styles['school-logo-type-default']
  );

  const hamburgerClassName = clsx(
    styles['hamburger-menu-wrapper'],
    isVariantThin ? styles['hamburger-menu-thin'] : styles['hamburger-menu-default']
  );

  return (
    <div className={styles.base}>
      <header className={headerClassName}>
        <Link href={logoHref} aria-label={LOGO_LINK_LABELS[logoHref] ?? LOGO_LINK_LABELS[PageUrl.TOP]}>
          <div className={logoMarkClassName}>
            <Icon name="LogoMark" size={isVariantThin ? 40 : 50} />
          </div>
          <div className={logoTypeClassName}>
            {logotypeType === 'school' ? (
              <div className={schoolLogoClassName}>
                <img
                  src="/img/school-logo-type.svg"
                  alt=""
                  className={styles['school-logo-type-image']}
                  width={238}
                  height={15}
                />
              </div>
            ) : logotypeType === 'nurseryWorks' ? (
              <div className={styles['nursery-works-logo-type']}>
                <NurseryWorksLogoType />
              </div>
            ) : (
              <div className={styles['home-montessori-logo-type']}>
                <HomeMontessoriLogoType />
              </div>
            )}
          </div>
        </Link>
      </header>
      {showMenuButton ? (
        <div className={hamburgerClassName}>
          <HamburgerMenuButton
            ref={triggerRef}
            menuId={menuId}
            accentColor={hamburgerAccentColor}
            isOpenMenu={isOpenMenu}
            onChangeMenuOpen={(isOpen: boolean) => {
              setIsOpenMenu(isOpen);
            }}
          />
        </div>
      ) : null}
      {isOpenMenu ? (
        <div className={styles['animation-wrapper']}>
          <Menu
            id={menuId}
            menuRef={menuRef}
            headerHeightPx={headerHeightPx}
            list={menuList}
            buttonList={buttonList}
            onClick={closeMenu}
          />
        </div>
      ) : null}
    </div>
  );
};

type MenuProps = {
  list?: Item[];
  onClick: () => void;
  headerHeightPx: number;
  buttonList?: Item[];
  id: string;
  menuRef: RefObject<HTMLDivElement>;
};

type Item = {
  href: string;
  text: string;
  iconName?: ComponentProps<typeof Icon>['name'];
  color?: ColorType;
  accentColor?: string;
};

const Menu = ({
  onClick,
  headerHeightPx,
  buttonList = [],
  id,
  menuRef,
  list = [
    {
      href: `${PageUrl.CLASS_TOP}#${SectionId.TEACHERS}`,
      text: '講師紹介',
    },
    {
      href: `${PageUrl.CLASS_TOP}#${SectionId.ABOUT}`,
      text: 'こだわり',
    },
    {
      href: `${PageUrl.CLASS_TOP}#${SectionId.APPEARANCE}`,
      text: '教室のようす',
    },
    {
      href: `${PageUrl.CLASS_TOP}#${SectionId.COURCES}`,
      text: 'コース紹介',
    },
    {
      href: `${PageUrl.CLASS_TOP}#${SectionId.INFORMATION}`,
      text: '教室情報',
    },
    {
      href: PageUrl.DAYCARE_TOP,
      text: 'モンテッソーリ保育園',
    },
  ],
}: MenuProps) => (
  <div ref={menuRef} id={id} className={styles['menu-base']} role="dialog" aria-modal="true" aria-label="メニュー">
    <ul className={styles['menu-list']} style={{ padding: `${headerHeightPx}px 2rem 2rem 2rem` }}>
      {list.map((item, index) => {
        const isLast = list.length === index + 1;
        const rowClassName = isLast ? styles['menu-row-last'] : styles['menu-row'];
        const iconFillStyle = item.color ? { fill: item.color } : { fill: Color.mainText };

        return (
          <li key={item.text} className={rowClassName} style={iconFillStyle}>
            <Link href={item.href} className={styles['menu-link']} onClick={onClick}>
              {item.iconName ? <Icon name={item.iconName} size={25} /> : null}
              <div>{item.text}</div>
            </Link>
          </li>
        );
      })}
    </ul>
    <div className={styles.divider} />
    <div className={styles['contact-us']}>
      <div className={styles['menu-title']}>お問い合せ</div>
      {buttonList.map((item) => {
        const isExternal = item.href.startsWith('https://') || item.href.startsWith('http://');
        return (
          <Link
            key={item.href}
            href={item.href}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            onClick={onClick}
          >
            <MainButton
              isHoverEffect
              isWidthFull
              buttonText={item.text}
              fontSizeType={FontSize.MEDIUM}
              shape="rectangle"
              backgroundColor={item.accentColor ?? Color.brandPink200}
              fontColor={item.accentColor ?? Color.brandPink500}
              borderColor={Color.brandPink500}
              valiant="border"
            />
          </Link>
        );
      })}
      <div className={styles['action-buttons-wrapper']}>
        <LineButton position="header" />
        <TelephoneButton />
      </div>
    </div>
  </div>
);
